/**
 * What an "Edit an item" Thread edits, as its step's `item` input names it (ADR-0031 D2): an
 * item at its exact version, or one term of an ontology module version, which the term card
 * edits in place. The input is text, so the agent in the Thread reads it as it is. Its one
 * grammar is the server's (`threads/domain/edited_items.py`); both read the cases of
 * `schema/edited-items/golden.json` the same way.
 */
export type EditedItem =
  /**
   * `<item id>@<version>`, with ` within <Workflow id>@<version>` when it is changed as that
   * Workflow version runs it: a version the project doesn't hold is then derived within it
   * (ADR-0037 D7).
   */
  | { readonly kind: "item"; readonly reference: string; readonly within?: string }
  /** `<module id>@<version>#<term IRI>`: the module version the term's edits go into. */
  | { readonly kind: "term"; readonly module: string; readonly iri: string };

const TERM_SEPARATOR = "#";
const WITHIN_SEPARATOR = " within ";
/**
 * An exact item version as the server's `VersionRef` reads it (`kernel/refs.py`): a UUID
 * version 7 in canonical form, then a 40 or 64 character Git object ID that isn't all zeros,
 * which names no object. Unlike the app's structural reference reading, the edit target checks
 * it, so the web app and the server agree on every text the golden cases hold.
 */
const ITEM_VERSION = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}@(?!0+$)(?:[0-9a-f]{40}|[0-9a-f]{64})$/;
const IRI = /^[A-Za-z][A-Za-z0-9+.-]*:\S+$/;

export function formatEditedItem(edited: EditedItem): string {
  if (edited.kind === "term") return `${edited.module}${TERM_SEPARATOR}${edited.iri}`;
  return edited.within === undefined ? edited.reference : `${edited.reference}${WITHIN_SEPARATOR}${edited.within}`;
}

/** The edited item a step's input names; `null` for text that names none. A term's IRI is absolute, so it has a scheme. */
export function parseEditedItem(text: string): EditedItem | null {
  const parts = text.split(WITHIN_SEPARATOR);
  if (parts.length > 1) {
    const [named = "", within = ""] = parts;
    return parts.length > 2 || !ITEM_VERSION.test(named) || !ITEM_VERSION.test(within) ? null : { kind: "item", reference: named, within };
  }
  const separator = text.indexOf(TERM_SEPARATOR);
  const reference = separator < 0 ? text : text.slice(0, separator);
  if (!ITEM_VERSION.test(reference)) return null;
  if (separator < 0) return { kind: "item", reference };
  const iri = text.slice(separator + 1);
  return IRI.test(iri) ? { kind: "term", module: reference, iri } : null;
}
